/**
 * Advanced fraud detection service
 * Integrates multiple fraud prevention techniques
 */

import { supabase } from "@/integrations/supabase/client";
import {
  generateDeviceFingerprint,
  detectSuspiciousDevice,
} from "./deviceFingerprinting";
import { getUserIPAddress } from "@/utils/geoIpService";

export interface FraudDetectionResult {
  allowed: boolean;
  riskLevel: "low" | "medium" | "high" | "critical";
  fraudReasons: string[];
  deviceTrustScore: number;
  ipRiskScore: number;
  recommendation: "allow" | "throttle" | "reject" | "block";
  sessionId: string;
  deviceFingerprint: string;
}

export interface StreamContext {
  userId: string;
  trackId: string;
  artistId: string;
  userAgent?: string;
}

/**
 * Main fraud detection function
 */
export async function detectStreamFraud(
  context: StreamContext,
): Promise<FraudDetectionResult> {
  console.log("🛡️ [FRAUD_DETECTION] Starting comprehensive fraud analysis:", {
    userId: context.userId,
    trackId: context.trackId,
    timestamp: new Date().toISOString(),
  });

  try {
    // Generate device fingerprint
    const deviceData = generateDeviceFingerprint();
    const suspiciousDevice = detectSuspiciousDevice(deviceData);

    // Get user's real IP address
    const ipAddress = (await getUserIPAddress()) || "0.0.0.0";

    // Update device fingerprint in database
    await updateDeviceFingerprint(context.userId, deviceData, ipAddress);

    // Run database-level fraud detection
    const { data: fraudResult, error } = await supabase.rpc(
      "detect_stream_fraud",
      {
        p_user_id: context.userId,
        p_track_id: context.trackId,
        p_device_fingerprint: deviceData.fingerprint,
        p_ip_address: ipAddress,
        p_user_agent: context.userAgent || deviceData.userAgent,
        p_session_id: generateSessionId(),
      },
    );

    if (error) {
      console.error(
        "❌ [FRAUD_DETECTION] Database fraud detection failed:",
        error,
      );
      // Fallback to client-side detection only
      return {
        allowed: !suspiciousDevice.suspicious,
        riskLevel: suspiciousDevice.suspicious ? "high" : "low",
        fraudReasons: suspiciousDevice.reasons,
        deviceTrustScore: suspiciousDevice.trustScore,
        ipRiskScore: 0,
        recommendation: suspiciousDevice.suspicious ? "reject" : "allow",
        sessionId: generateSessionId(),
        deviceFingerprint: deviceData.fingerprint,
      };
    }

    const result = fraudResult as any;
    console.log("✅ [FRAUD_DETECTION] Fraud analysis complete:", {
      fraudDetected: result.fraud_detected,
      riskLevel: result.risk_level,
      recommendation: result.recommendation,
      deviceTrustScore: result.device_trust_score,
      ipRiskScore: result.ip_risk_score,
    });

    return {
      allowed: !result.fraud_detected,
      riskLevel: result.risk_level,
      fraudReasons: result.fraud_reasons || [],
      deviceTrustScore: result.device_trust_score,
      ipRiskScore: result.ip_risk_score,
      recommendation: result.recommendation,
      sessionId: generateSessionId(),
      deviceFingerprint: deviceData.fingerprint,
    };
  } catch (error) {
    console.error("🔥 [FRAUD_DETECTION] Exception in fraud detection:", error);

    // Fallback: allow stream but with medium risk
    return {
      allowed: true,
      riskLevel: "medium",
      fraudReasons: ["Fraud detection system error"],
      deviceTrustScore: 50,
      ipRiskScore: 0,
      recommendation: "throttle",
      sessionId: generateSessionId(),
      deviceFingerprint: "error",
    };
  }
}

/**
 * Record successful stream for duplicate prevention
 */
export async function recordStreamForDuplicatePrevention(
  userId: string,
  trackId: string,
  deviceFingerprint: string,
  sessionId: string,
  streamDuration: number,
): Promise<void> {
  try {
    const ipAddress = (await getUserIPAddress()) || "0.0.0.0";

    await supabase.rpc("record_stream_for_duplicate_prevention", {
      p_user_id: userId,
      p_track_id: trackId,
      p_device_fingerprint: deviceFingerprint,
      p_ip_address: ipAddress,
      p_session_id: sessionId,
      p_stream_duration: streamDuration,
    });

    console.log(
      "📝 [FRAUD_PREVENTION] Stream recorded for duplicate prevention:",
      {
        userId,
        trackId,
        deviceFingerprint,
        sessionId,
        streamDuration,
      },
    );
  } catch (error) {
    console.error(
      "❌ [FRAUD_PREVENTION] Failed to record stream for duplicate prevention:",
      error,
    );
  }
}

/**
 * Update device fingerprint in database
 */
async function updateDeviceFingerprint(
  userId: string,
  deviceData: any,
  ipAddress: string,
): Promise<void> {
  try {
    await supabase.rpc("update_device_fingerprint", {
      p_user_id: userId,
      p_device_fingerprint: deviceData.fingerprint,
      p_user_agent: deviceData.userAgent,
      p_screen_resolution: deviceData.screenResolution,
      p_timezone: deviceData.timezone,
      p_language: deviceData.language,
      p_platform: deviceData.platform,
      p_ip_address: ipAddress,
    });
  } catch (error) {
    console.error(
      "❌ [FRAUD_PREVENTION] Failed to update device fingerprint:",
      error,
    );
  }
}

/**
 * Generate session ID
 */
function generateSessionId(): string {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
}

/**
 * Update user behavioral patterns (for future ML analysis)
 */
export async function updateUserBehavior(
  userId: string,
  streamData: {
    trackId: string;
    duration: number;
    artistId: string;
    timestamp: Date;
  },
): Promise<void> {
  try {
    // This would typically update behavioral patterns
    // For now, we'll just log it
    console.log("📊 [BEHAVIORAL_ANALYSIS] Updating user behavior patterns:", {
      userId,
      ...streamData,
    });
  } catch (error) {
    console.error(
      "❌ [BEHAVIORAL_ANALYSIS] Failed to update user behavior:",
      error,
    );
  }
}
